/**
 * Minimal Security Service
 * Lightweight security checks for performance-critical operations
 */

class MinimalSecurityService {
  constructor() {
    this.threatPatterns = {
      sqlInjection: [
        /('|;|\*|--|\/\*|\*\/)/i,
        /(union|select|insert|update|delete|drop|create|alter)/i,
        /(or|and)\s+\d+\s*=\s*\d+/i
      ],
      xss: [
        /<script[^>]*>.*?<\/script>/gi,
        /javascript:/gi,
        /on\w+\s*=/gi,
        /<iframe[^>]*>.*?<\/iframe>/gi
      ],
      pathTraversal: [
        /\.\.\//g,
        /\.\.\\/g,
        /\.\.%2f/gi,
        /\.\.%5c/gi
      ],
      commandInjection: [
        /[;&|`$()]/g,
        /rm\s+-rf/i,
        /del\s+\/s/i,
        /cmd\s*\/c/i
      ]
    };
  }

  /**
   * Fast security check for performance-critical operations
   * @param {string} input - Input to check
   * @param {string} type - Type of input (query, address, etc.)
   * @param {string} clientIP - Client IP address
   * @returns {Object} Security check result
   */
  fastSecurityCheck(input, type = 'query', clientIP = 'unknown') {
    const startTime = performance.now();
    
    // Quick length check first (fastest)
    if (input.length > 1000) {
      return {
        isValid: false,
        threats: ['input_too_long'],
        sanitized: input.substring(0, 1000),
        performance: performance.now() - startTime
      };
    }

    // Fast pattern checks
    const threats = this.quickThreatCheck(input);
    
    if (threats.length > 0) {
      return {
        isValid: false,
        threats,
        sanitized: this.basicSanitize(input),
        performance: performance.now() - startTime
      };
    }

    // Basic sanitization
    const sanitized = this.basicSanitize(input);
    
    return {
      isValid: true,
      threats: [],
      sanitized,
      performance: performance.now() - startTime
    };
  }

  /**
   * Quick threat check for performance
   * @param {string} input - Input to check
   * @returns {Array} Detected threats
   */
  quickThreatCheck(input) {
    const threats = [];
    
    // Check SQL injection patterns
    for (const pattern of this.threatPatterns.sqlInjection) {
      if (pattern.test(input)) {
        threats.push('sql_injection');
        break;
      }
    }
    
    // Check XSS patterns
    for (const pattern of this.threatPatterns.xss) {
      if (pattern.test(input)) {
        threats.push('xss');
        break;
      }
    }
    
    // Check path traversal
    for (const pattern of this.threatPatterns.pathTraversal) {
      if (pattern.test(input)) {
        threats.push('path_traversal');
        break;
      }
    }
    
    // Check command injection
    for (const pattern of this.threatPatterns.commandInjection) {
      if (pattern.test(input)) {
        threats.push('command_injection');
        break;
      }
    }
    
    return threats;
  }

  /**
   * Basic sanitization for performance
   * @param {string} input - Input to sanitize
   * @returns {string} Sanitized input
   */
  basicSanitize(input) {
    return input
      .replace(/[<>'"&]/g, '') // Remove HTML/XML characters
      .replace(/[;&|`$()]/g, '') // Remove command injection characters
      .replace(/\.\.\//g, '') // Remove path traversal
      .substring(0, 1000) // Limit length
      .trim();
  }

  /**
   * Mask sensitive data for logging
   * @param {string} data - Data to mask
   * @returns {string} Masked data
   */
  maskSensitiveData(data) {
    if (!data || typeof data !== 'string') return data;
    
    // Mask email addresses
    data = data.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '***@***.***');
    
    // Mask phone numbers
    data = data.replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, '***-***-****');
    
    // Mask credit card numbers
    data = data.replace(/\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, '****-****-****-****');
    
    return data;
  }

  /**
   * Validate input length
   * @param {string} input - Input to validate
   * @param {number} maxLength - Maximum allowed length
   * @returns {boolean} True if valid
   */
  validateLength(input, maxLength = 1000) {
    return input && input.length <= maxLength;
  }

  /**
   * Validate input type
   * @param {string} input - Input to validate
   * @param {string} type - Expected type
   * @returns {boolean} True if valid
   */
  validateType(input, type) {
    if (!input || typeof input !== 'string') return false;
    
    switch (type) {
      case 'query':
        return this.validateLength(input, 100) && /^[a-zA-Z0-9\s\-.,]+$/.test(input);
      case 'address':
        return this.validateLength(input, 200) && /^[a-zA-Z0-9\s\-.,#]+$/.test(input);
      case 'email':
        return this.validateLength(input, 100) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input);
      case 'phone':
        return this.validateLength(input, 20) && /^[\d\-\+\(\)\s]+$/.test(input);
      default:
        return this.validateLength(input);
    }
  }

  /**
   * Get security statistics
   * @returns {Object} Security statistics
   */
  getSecurityStats() {
    return {
      threatPatterns: Object.keys(this.threatPatterns).length,
      totalPatterns: Object.values(this.threatPatterns).reduce((sum, patterns) => sum + patterns.length, 0)
    };
  }
}

export default new MinimalSecurityService();